export type ApiEnvelope<T> = {
  data: T;
};

function isApiEnvelope<T>(payload: T | ApiEnvelope<T>): payload is ApiEnvelope<T> {
  return typeof payload === "object" && payload !== null && "data" in payload;
}

export function unwrapApiResponse<T>(payload: T | ApiEnvelope<T>): T {
  return isApiEnvelope(payload) ? payload.data : payload;
}

export function normalizeAvailableDateSlots(input: any[]) {
  if (!Array.isArray(input)) return [];

  return input
    .map((item) => {
      const date = (item?.date || item?.available_date || "").toString().trim();
      const rawSlots = Array.isArray(item?.slots)
        ? item.slots
        : Array.isArray(item?.time_slots)
          ? item.time_slots
          : [];
      const slots = rawSlots
        .map((slot) => String(slot || "").trim())
        .filter(Boolean);

      if (!date || !slots.length) return null;
      return { date, slots };
    })
    .filter(Boolean);
}

export function normalizeCategories(input: any[]) {
  if (!Array.isArray(input)) return [];

  return input
    .map((category) => normalizeTreeNode(category, true))
    .filter(Boolean)
    .map((category) => ({
      ...category,
      services: category.services || [],
    }));
}

export function extractHierarchyData<T>(response: T[] | ApiEnvelope<T[]>) {
  if (isApiEnvelope<T[]>(response) && Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response)) return response;
  return [];
}

export function extractAvailableDatesData<T>(
  response: T[] | ApiEnvelope<T[]>,
) {
  if (isApiEnvelope<T[]>(response) && Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response)) return response;
  return [];
}

function normalizeTreeNode(node: any, isCategory = false): any {
  if (!node || typeof node !== "object") return null;

  const name = String(node.name || "").trim();
  const id = String(node.id || toId(name)).trim();
  if (!name || !id) return null;

  const shortInfo = String(
    node.short_info || node.shortInfo || node.description || "",
  ).trim();
  const formQuestion = String(
    node.form_question || node.formQuestion || node.item_prompt || "",
  ).trim();

  const rawChildren = isCategory
    ? node.services || node.items || []
    : node.child_items || node.childItems || node.items || [];

  const children = Array.isArray(rawChildren)
    ? rawChildren
        .map((child) => normalizeTreeNode(child, false))
        .filter(Boolean)
    : [];

  if (isCategory) {
    return {
      id,
      name,
      short_info: shortInfo,
      form_question: formQuestion,
      services: children,
    };
  }

  return {
    id,
    name,
    short_info: shortInfo,
    form_question: formQuestion,
    child_items: children,
  };
}

function toId(value: string) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
